import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {ArrowLeft, Home, Bed, Sofa, Package, MapPin, Euro, Mail, UserPlus, UserCheck, Clock as ClockIcon} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'
import Navbar from '@/components/Navbar'
import Footer from '@/components/Footer'

interface Anuncio {
  _id: string
  titulo: string
  descricao: string
  categoria: string
  preco: number
  localizacao: string
  fotos?: string[]
  imagem?: string
  createdAt?: string
}

interface UsuarioInfo {
  nome: string
  foto: string
  email: string
  bio: string
  anuncios: Anuncio[]
}

// Dados mockados de usuários (fallback)
const usuariosMock: Record<string, UsuarioInfo> = {
  'mock-1': {
    nome: 'Marco Silva',
    foto: 'https://i.pravatar.cc/150?img=12',
    email: 'marco.silva@email.com',
    bio: 'Brasileiro em Milao ha 3 anos.',
    anuncios: []
  }
}

function MarketplaceUsuario() {
  const { usuarioId } = useParams<{ usuarioId: string }>()
  const { user, isAuthenticated } = useAuth()
  const [usuario, setUsuario] = useState<UsuarioInfo | null>(null)
  const [loading, setLoading] = useState(true)
  // Estado de amizade: 'nenhum' | 'pendente_enviada' | 'pendente_recebida' | 'amigos' | 'proprio'
  const [statusAmizade, setStatusAmizade] = useState<'nenhum' | 'pendente_enviada' | 'pendente_recebida' | 'amigos' | 'proprio'>('nenhum')
  const [enviandoAmizade, setEnviandoAmizade] = useState(false)

  useEffect(() => {
    const fetchUsuarioAnuncios = async () => {
      if (!usuarioId) return
      setLoading(true)
      try {
        const { list } = await lumi.entities.marketplace_anuncios.list({
          filter: { userId: usuarioId },
          sort: { createdAt: -1 }
        })
        if (list && list.length > 0) {
          const primeiro = list[0]
          setUsuario({
            nome: primeiro.userName || 'Usuario',
            foto: primeiro.userPhoto || `https://i.pravatar.cc/150?u=${usuarioId}`,
            email: primeiro.userEmail || '',
            bio: 'Membro da comunidade brasileira em Milao.',
            anuncios: list as Anuncio[]
          })
        } else if (usuariosMock[usuarioId]) {
          setUsuario(usuariosMock[usuarioId])
        } else {
          setUsuario(null)
        }
      } catch (error) {
        console.error('Erro ao carregar usuario:', error)
        setUsuario(null)
      } finally {
        setLoading(false)
      }
    }
    fetchUsuarioAnuncios()
  }, [usuarioId])

  // Verificar status de amizade entre o usuario logado e o usuario da pagina
  useEffect(() => {
    const verificarAmizade = async () => {
      if (!usuarioId || !user?.userId) return
      if (usuarioId === user.userId) {
        setStatusAmizade('proprio')
        return
      }
      try {
        const { list } = await lumi.entities.amizades.list({ sort: { createdAt: -1 } })
        const relacao = (list || []).find((a: any) =>
          (a.solicitanteId === user.userId && a.destinatarioId === usuarioId) ||
          (a.solicitanteId === usuarioId && a.destinatarioId === user.userId)
        )
        if (!relacao) {
          setStatusAmizade('nenhum')
        } else if (relacao.status === 'aceito') {
          setStatusAmizade('amigos')
        } else if (relacao.solicitanteId === user.userId) {
          setStatusAmizade('pendente_enviada')
        } else {
          setStatusAmizade('pendente_recebida')
        }
      } catch (e) {
        console.error('Erro ao verificar amizade:', e)
      }
    }
    verificarAmizade()
  }, [usuarioId, user?.userId])

  const handleFazerAmizade = async () => {
    if (!usuarioId || !user?.userId || !usuario) return
    setEnviandoAmizade(true)
    try {
      const now = new Date().toISOString()
      // Buscar perfil do usuario logado para preencher dados do solicitante
      let solicitanteFoto = ''
      let solicitanteBanner = ''
      try {
        const { list: meuPerfil } = await lumi.entities.user_profiles.list({ filter: { userId: user.userId }, limit: 1 })
        if (meuPerfil && meuPerfil.length > 0) {
          solicitanteFoto = meuPerfil[0].profileImageUrl || ''
          solicitanteBanner = meuPerfil[0].bannerUrl || ''
        }
      } catch (e) { /* segue sem foto */ }

      await lumi.entities.amizades.create({
        solicitanteId: user.userId,
        solicitanteNome: user.userName || user.email || 'Usuario',
        solicitanteFoto,
        solicitanteBanner,
        destinatarioId: usuarioId,
        destinatarioNome: usuario.nome,
        destinatarioFoto: usuario.foto || '',
        destinatarioBanner: '',
        status: 'pendente',
        creator: user.userId,
        createdAt: now,
        updatedAt: now
      })
      setStatusAmizade('pendente_enviada')
    } catch (e) {
      console.error('Erro ao enviar solicitacao de amizade:', e)
    } finally {
      setEnviandoAmizade(false)
    }
  }

  const getCategoriaIcon = (categoria: string) => {
    switch (categoria) {
      case 'quarto': return <Home className="w-5 h-5" />
      case 'cama': return <Bed className="w-5 h-5" />
      case 'apartamento': return <Sofa className="w-5 h-5" />
      case 'desapego': return <Package className="w-5 h-5" />
      default: return <Package className="w-5 h-5" />
    }
  }

  const getCategoriaLabel = (categoria: string) => {
    switch (categoria) {
      case 'quarto': return 'Quarto'
      case 'cama': return 'Cama'
      case 'apartamento': return 'Apartamento'
      case 'desapego': return 'Desapego'
      default: return categoria
    }
  }

  const getImagem = (anuncio: Anuncio): string => {
    if (anuncio.fotos && anuncio.fotos.length > 0) return anuncio.fotos[0]
    if (anuncio.imagem) return anuncio.imagem
    return 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-20 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-[#E53935] border-t-transparent rounded-full animate-spin" />
        </div>
        <Footer />
      </div>
    )
  }

  if (!usuario) {
    return (
      <div className="min-h-screen bg-[#111111]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Usuário não encontrado</h2>
            <Link
              to="/marketplace"
              className="inline-flex items-center gap-2 text-[#E53935] hover:underline font-semibold"
            >
              <ArrowLeft className="w-5 h-5" />
              Voltar ao Marketplace
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#111111]">
      <Navbar />

      {/* Banner do anunciante */}
      <div className="bg-gradient-to-r from-[#1a0a00] via-[#2c1a0e] to-[#1a0a00] border-b-2 border-[#5D4037] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-[#E53935] font-semibold mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar ao Marketplace
          </Link>
          <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
            <img
              src={usuario.foto}
              alt={usuario.nome}
              className="w-28 h-28 rounded-full border-4 border-[#E53935] shadow-lg shadow-[#E53935]/20 object-cover"
            />
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-4xl font-black text-white mb-2">
                {usuario.nome}
              </h1>
              <p className="text-gray-400 text-lg mb-4">{usuario.bio}</p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                {usuario.email && (
                  <a
                    href={`mailto:${usuario.email}`}
                    className="inline-flex items-center gap-2 bg-[#2a2a2a] border border-[#3a2a1e] hover:border-[#E53935] text-gray-300 hover:text-white px-4 py-2 rounded-lg font-semibold transition-all"
                  >
                    <Mail className="w-4 h-4 text-[#E53935]" />
                    {usuario.email}
                  </a>
                )}

                {/* Botao Fazer amizade */}
                {isAuthenticated && statusAmizade !== 'proprio' && (
                  statusAmizade === 'nenhum' ? (
                    <button
                      onClick={handleFazerAmizade}
                      disabled={enviandoAmizade}
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-5 py-2 rounded-lg font-semibold transition-all hover:shadow-lg hover:shadow-[#E53935]/30 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {enviandoAmizade ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <UserPlus className="w-4 h-4" />
                      )}
                      {enviandoAmizade ? 'Enviando...' : 'Fazer amizade'}
                    </button>
                  ) : statusAmizade === 'pendente_enviada' ? (
                    <span className="inline-flex items-center gap-2 bg-[#2a2a2a] border border-[#E57335]/40 text-[#E57335] px-5 py-2 rounded-lg font-semibold">
                      <ClockIcon className="w-4 h-4" />
                      Solicitacao enviada
                    </span>
                  ) : statusAmizade === 'pendente_recebida' ? (
                    <span className="inline-flex items-center gap-2 bg-[#2a2a2a] border border-[#E57335]/40 text-[#E57335] px-5 py-2 rounded-lg font-semibold">
                      <ClockIcon className="w-4 h-4" />
                      Aguardando sua resposta
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 bg-green-900/30 border border-green-600/40 text-green-400 px-5 py-2 rounded-lg font-semibold">
                      <UserCheck className="w-4 h-4" />
                      Amigos
                    </span>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Título */}
        <div className="mb-8">
          <h2 className="text-3xl font-black text-white">
            Anúncios de {usuario.nome.split(' ')[0]}
            <span className="text-[#E53935] ml-2">({usuario.anuncios.length})</span>
          </h2>
        </div>

        {usuario.anuncios.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {usuario.anuncios.map((anuncio) => (
              <div
                key={anuncio._id}
                className="bg-[#1e1e1e] border border-[#3a2a1e] rounded-xl overflow-hidden hover:border-[#E53935] hover:shadow-2xl hover:shadow-[#E53935]/10 transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Imagem */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={getImagem(anuncio)}
                    alt={anuncio.titulo}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute top-3 right-3 bg-[#E53935] text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                    {getCategoriaIcon(anuncio.categoria)}
                    {getCategoriaLabel(anuncio.categoria)}
                  </div>
                </div>

                {/* Conteúdo */}
                <div className="p-5">
                  <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">
                    {anuncio.titulo}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {anuncio.descricao}
                  </p>

                  {/* Localização */}
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                    <MapPin className="w-4 h-4 text-[#E53935]" />
                    {anuncio.localizacao}
                  </div>

                  {/* Preço */}
                  <div className="flex items-center gap-2 mb-4">
                    <Euro className="w-5 h-5 text-[#E53935]" />
                    <span className="text-2xl font-black text-[#E53935]">
                      {anuncio.preco}
                    </span>
                    <span className="text-gray-500 text-sm">/mês</span>
                  </div>

                  <button className="w-full bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-4 py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-[#E53935]/30 transition-all">
                    Tenho Interesse
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#1e1e1e] border border-[#3a2a1e] rounded-xl p-12 text-center">
            <Package className="w-16 h-16 text-[#5D4037] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-300 mb-2">
              Nenhum anúncio ativo
            </h3>
            <p className="text-gray-500">
              Este usuário ainda não publicou anúncios no marketplace.
            </p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  )
}

export default MarketplaceUsuario
